import { useEventUpdateMeetingTime } from "@crowdcoursing/api/openapi";
import { useCallback } from "react";

import { useToast } from "./useToast";

interface UseEventMeetingTimesProps {
  eventId: string;
  event?: {
    meetingTimes: {
      id: string;
      beginMeetingTime: string;
      endMeetingTime: string;
      capacity: number;
      seriesId?: string | null;
    }[];
  } | null;
  refetchEvent: () => Promise<unknown>;
}

export const useEventMeetingTimes = ({ eventId, event, refetchEvent }: UseEventMeetingTimesProps) => {
  const toast = useToast();

  const { mutateAsync: updateMeetingTimeMutation } = useEventUpdateMeetingTime();

  const handleUpdateMeetingTime = useCallback(
    async (
      meetingTimeId: string,
      updates: {
        seriesId?: string | null;
        capacity?: number | null;
        beginTime?: string;
        endTime?: string;
      },
    ) => {
      if (!event) {
        return;
      }

      const meetingTime = event.meetingTimes.find((mt) => mt.id === meetingTimeId);
      if (!meetingTime) {
        toast.error("Meeting time not found");
        return;
      }

      await updateMeetingTimeMutation(
        {
          body: {
            beginMeetingTime: updates.beginTime || meetingTime.beginMeetingTime,
            endMeetingTime: updates.endTime || meetingTime.endMeetingTime,
            maximumSlots: updates.capacity !== undefined ? updates.capacity : meetingTime.capacity,
            meetingTimeSeriesId: updates.seriesId !== undefined ? updates.seriesId : meetingTime.seriesId,
          },
          pathParams: {
            eventId,
            meetingId: meetingTimeId,
          },
        },
        {
          onError: (error) => {
            console.error("Failed to update meeting time:", error);
            toast.error("Failed to update meeting time. Please try again.");
          },
          onSuccess: () => {
            toast.success("Successfully updated meeting time!");
            refetchEvent();
          },
        },
      );
    },
    [eventId, event, refetchEvent, updateMeetingTimeMutation, toast],
  );

  return {
    handleUpdateMeetingTime,
  };
};
